// Pure utility functions for HexWorldMap — no React dependencies.
// Generic constraints used instead of local interfaces to avoid name conflicts.

export function calculateHexDistance(
  tile1: { x: number; y: number } | null,
  tile2: { x: number; y: number }
): number {
  if (!tile1) return 0;
  const q1 = tile1.x, r1 = tile1.y, s1 = -q1 - r1;
  const q2 = tile2.x, r2 = tile2.y, s2 = -q2 - r2;
  return (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs(s1 - s2)) / 2;
}

export function getTownAtTile<T extends { worldX: number; worldY: number }>(
  towns: T[], x: number, y: number
): T | undefined {
  return towns.find(t => t.worldX === x && t.worldY === y);
}

export function getTownIcon(townType: string): string {
  switch (townType) {
    case 'Mining':   return '⛏️';
    case 'Forest':   return '🌲';
    case 'Trading':  return '🏪';
    case 'Military': return '⚔️';
    case 'Magical':  return '🔮';
    default:         return '🏰';
  }
}

export function getRarityColor(rarity: number): string {
  const colors = ['#9e9e9e', '#4caf50', '#2196f3', '#9c27b0', '#ff9800'];
  return colors[rarity - 1] || '#666';
}

export function getNearestTownHint(
  tileX: number,
  tileY: number,
  towns: Array<{ worldX: number; worldY: number }>
): { bearingDeg: number; opacity: number } | null {
  const MAX_DISTANCE = 6;
  let nearest: { worldX: number; worldY: number } | null = null;
  let nearestDist = Infinity;

  for (const town of towns) {
    const dist = calculateHexDistance({ x: tileX, y: tileY }, { x: town.worldX, y: town.worldY });
    if (dist > 0 && dist <= MAX_DISTANCE && dist < nearestDist) {
      nearest = town;
      nearestDist = dist;
    }
  }

  if (!nearest) return null;

  // Compute direction in screen space for pointy-top axial hex (cx ∝ √3*(q+r/2), cy ∝ 1.5*r)
  const dq = nearest.worldX - tileX;
  const dr = nearest.worldY - tileY;
  const scrDX = Math.sqrt(3) * (dq + dr / 2);
  const scrDY = 1.5 * dr;
  const rawBearing = Math.atan2(scrDY, scrDX);

  // Tier by distance: far = faint + coarse direction; near = sharp + true bearing
  let opacity: number;
  let snapStep: number;
  if (nearestDist >= 5) {
    opacity = 0.35;
    snapStep = Math.PI / 2; // 4 cardinals
  } else if (nearestDist >= 3) {
    opacity = 0.55;
    snapStep = Math.PI / 4; // 8 directions
  } else {
    opacity = 0.75;
    snapStep = 0;            // true bearing
  }

  const bearingDeg = snapStep > 0
    ? Math.round(rawBearing / snapStep) * snapStep * (180 / Math.PI)
    : rawBearing * (180 / Math.PI);

  return { bearingDeg, opacity };
}

export function getVisibleTiles<T extends { id: number; x: number; y: number }>(
  tiles: T[], currentTileId: number, viewRadius: number
): T[] {
  if (tiles.length === 0) return [];
  const currentTile = tiles.find(t => t.id === currentTileId);
  if (!currentTile) return tiles;
  return tiles.filter(tile => {
    const dx = tile.x - currentTile.x;
    const dy = tile.y - currentTile.y;
    return Math.sqrt(dx * dx + dy * dy) <= viewRadius;
  });
}
